import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styles from './BrandsTable.module.css';
import InternalChat from './InternalChat';
import BrandMetadataModal from './BrandMetadataModal';
import PaymentLinkModal from './PaymentLinkModal';
import { getAllBrandUnreadCounts } from '../features/internalChat/internalChatSlice';

// Demo brands data
export const DEMO_BRANDS = [
  { id: 1, name: 'EcoWear', skus: 24, subscription: 'Pro', inspire: true, products: true, content: true },
  { id: 2, name: 'GreenThreads', skus: 12, subscription: 'Basic', inspire: true, products: true, content: true },
  { id: 3, name: 'PureEarth', skus: 18, subscription: 'Premium', inspire: true, products: true, content: true },
  { id: 4, name: 'UrbanRoots', skus: 9, subscription: 'Pro', inspire: true, products: true, content: true },
  { id: 5, name: 'Forest Hill', skus: 15, subscription: 'Premium', inspire: true, products: true, content: true },
  { id: 6, name: 'BluePlanet', skus: 21, subscription: 'Premium', inspire: true, products: true, content: true },
  { id: 7, name: 'LeafyLane', skus: 7, subscription: 'Pro', inspire: true, products: true, content: true },
  { id: 8, name: 'Sunrise Apparel', skus: 13, subscription: 'Basic', inspire: true, products: true, content: true },
  { id: 9, name: 'EarthKind', skus: 16, subscription: 'Premium', inspire: true, products: true, content: true },
  { id: 10, name: 'NatureNest', skus: 11, subscription: 'Pro', inspire: true, products: true, content: true },
];

const columns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'skus', label: 'No. of SKUs', sortable: true },
  { key: 'subscription', label: 'Subscription', sortable: true },
  { key: 'certification', label: '3SC Certification', sortable: true },
  { key: 'inspire', label: 'Inspire Page', clickable: true },
  { key: 'products', label: 'Products & Data', clickable: true },
  { key: 'lastEmail', label: 'Performance Email' },
  { key: 'messages', label: 'Conversation' },
  { key: 'paymentLink', label: 'Payment Link' }, // New column for employees
  { key: 'metadata', label: 'Metadata' },
];

export default function BrandsTable({ brands = DEMO_BRANDS }) {
  console.log('BrandsTable received:', brands); // Debug log
  
  const dispatch = useDispatch();
  const { brandUnreadCounts } = useSelector(state => state.internalChat);
  
  // Map API brands to table format - include certification data
  const mappedBrands = brands.map(b => ({
    id: b.id,
    name: b.name,
    skus: b.skus || 'N/A',
    subscription: b.subscription || 'Basic',
    certification: b.certification_status || { status: 'eligible' },
    inspire: !!b.domain,
    products: !!b.shopify_vendor_name,
    lastEmail: 'N/A',
    messages: '',
  }));
    
  console.log('Mapped brands:', mappedBrands); // Debug log

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [openChatBrand, setOpenChatBrand] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [selectedBrandForPayment, setSelectedBrandForPayment] = useState(null);
  const navigate = useNavigate();
  const userType = useSelector((state) => state.user.user_type);

  // Note: Unread counts are now fetched only when needed (when opening chat modals)
  // This prevents unnecessary API calls on brands page load

  const filtered = useMemo(() => {
    let data = mappedBrands.filter(b => b.name && b.name.toLowerCase().includes(search.toLowerCase()));
    data = [...data].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return sortDir === 'asc' ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [mappedBrands, search, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleMetadataUpdateSuccess = (updatedBrand) => {
    // You could update the brands list here if needed
    console.log('Brand metadata updated:', updatedBrand);
  };

  const renderCertificationStatus = (certification) => {
    if (!certification) {
      return (
        <span className={styles.statusBadge + ' ' + styles.statusEligible}>
          Eligible
        </span>
      );
    }

    const { status, grade, percentage_score, expires_at } = certification;

    if (status === 'certified') {
      const isExpired = expires_at && new Date(expires_at) < new Date();
      
      return (
        <div className={styles.certificationCell}>
          <span className={`${styles.statusBadge} ${styles.statusCertified}`}>
            Certified
          </span>
          <div className={`${styles.gradeBadge} ${styles['grade' + grade?.replace(/[+-]/g, '')]}`}>
            {grade}
          </div>
          <div className={styles.scoreText}>
            {percentage_score}%
          </div>
          {isExpired && (
            <div className={styles.expiredNote}>Expired</div>
          )}
        </div>
      );
    }

    if (status === 'eligible') {
      return (
        <span className={styles.statusBadge + ' ' + styles.statusEligible}>
          Eligible
        </span>
      );
    }

    if (status === 'expired') {
      return (
        <div className={styles.certificationCell}>
          <span className={styles.statusBadge + ' ' + styles.statusExpired}>
            Expired
          </span>
          {grade && (
            <div className={styles.gradeBadge + ' ' + styles.expiredGrade}>
              {grade}
            </div>
          )}
        </div>
      );
    }

    return (
      <span className={styles.statusBadge + ' ' + styles.statusUnknown}>
        Unknown
      </span>
    );
  };

  // Filter columns based on user type
  const visibleColumns = columns.filter(col => {
    if (col.key === 'metadata' && userType !== 'employee') {
      return false;
    }
    if (col.key === 'paymentLink' && userType !== 'employee') {
      return false;
    }
    return true;
  });

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <input
          className={styles.searchBox}
          type="text"
          placeholder="Search by brand name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            {visibleColumns.map(col => (
              <th
                key={col.key}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
                className={col.sortable ? styles.sortable : ''}
                style={{ cursor: col.sortable ? 'pointer' : 'default' }}
              >
                {col.label}
                {col.sortable && sortKey === col.key && (
                  <span className={styles.sortArrow}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map(brand => (
            <tr key={brand.id} className={brand.name === 'Forest Hill' && brand.subscription === 'Premium' ? styles.premiumRow : ''}>
              <td>{brand.name}</td>
              <td>{brand.skus}</td>
              <td>{brand.subscription}</td>
              <td>
                {renderCertificationStatus(brand.certification)}
              </td>
              <td>
                <button 
                  className={styles.linkBtn} 
                  onClick={() => navigate(`/brands/${brand.id}/inspire-page`)}
                >
                  View
                </button>
              </td>
              <td>
                <button 
                  className={styles.linkBtn} 
                  onClick={() => navigate(`/brands/${brand.id}/products`)}
                >
                  View
                </button>
              </td>
              <td>
                <button className={styles.linkBtn} onClick={() => navigate(`/brands/${brand.id}/email-calendar`)}>View</button>
              </td>
              <td>
                <button
                  className={styles.linkBtn}
                  title="Open Messages"
                  onClick={() => {
                    setOpenChatBrand(brand);
                    // Fetch unread counts when opening chat
                    if (userType === 'employee') {
                      dispatch(getAllBrandUnreadCounts());
                    }
                  }}
                  style={{
                    display:'flex',
                    alignItems:'center',
                    gap:4,
                    position:'relative',
                    background: brandUnreadCounts[brand.id] > 0 ? '#fff3cd' : undefined,
                    border: brandUnreadCounts[brand.id] > 0 ? '1px solid #ffeaa7' : undefined
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="6" width="14" height="7" rx="3.5" fill="#00bfae" />
                    <circle cx="8" cy="9.5" r="1" fill="#fff" />
                    <circle cx="12" cy="9.5" r="1" fill="#fff" />
                  </svg>
                  Messages
                  {/* Unread count indicator */}
                  {brandUnreadCounts[brand.id] > 0 && (
                    <span style={{
                      background: '#dc3545',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      marginLeft: '4px',
                      minWidth: '18px',
                      textAlign: 'center',
                      lineHeight: '1'
                    }}>
                      {brandUnreadCounts[brand.id]}
                    </span>
                  )}
                </button>
              </td>
              {/* Only show payment link column for employees */}
              {userType === 'employee' && (
                <td>
                  <button 
                    className={styles.linkBtn}
                    onClick={() => {
                      setSelectedBrandForPayment(brand);
                      setShowPaymentLinkModal(true);
                    }}
                    title="Generate Payment Link"
                  >
                    🔗 Generate Link
                  </button>
                </td>
              )}
              {/* Only show metadata column for employees */}
              {userType === 'employee' && (
                <td>
                  <button 
                    className={styles.linkBtn}
                    onClick={() => {
                      setSelectedBrandId(brand.id);
                      setShowMetadataModal(true);
                    }}
                    title="Manage Brand Metadata"
                  >
                    Manage
                  </button>
                </td>
              )}
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={visibleColumns.length} style={{textAlign:'center',color:'#888'}}>No brands found.</td></tr>
          )}
        </tbody>
      </table>
      {/* Modal for brand chat */}
      {openChatBrand && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.18)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => {
          setOpenChatBrand(null);
          // Refresh unread counts when closing chat
          if (userType === 'employee') {
            dispatch(getAllBrandUnreadCounts());
          }
        }}>
          <div style={{background:'#fff',borderRadius:16,padding:'0',minWidth:340,maxWidth:600,width:'100%',boxShadow:'0 4px 32px rgba(60,60,90,0.18)',position:'relative',display:'flex',flexDirection:'column',alignItems:'stretch',minHeight:400}} onClick={e=>e.stopPropagation()}>
            <button onClick={() => {
              setOpenChatBrand(null);
              // Refresh unread counts when closing chat
              if (userType === 'employee') {
                dispatch(getAllBrandUnreadCounts());
              }
            }} style={{position:'absolute',top:12,right:16,background:'#e6f4f1',color:'#004443',border:'none',borderRadius:8,padding:'0.3rem 1rem',fontWeight:700,fontSize:'1.3rem',cursor:'pointer',zIndex:2}}>×</button>
            <div style={{padding:'1.2rem 1.5rem 0.5rem 1.5rem',fontWeight:700,fontSize:'1.2rem',color:'#004443'}}>
              Messages: {openChatBrand.name}
              {brandUnreadCounts[openChatBrand.id] > 0 && (
                <span style={{
                  background: '#dc3545',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  padding: '2px 8px',
                  marginLeft: '8px'
                }}>
                  {brandUnreadCounts[openChatBrand.id]} unread
                </span>
              )}
            </div>
            <div style={{flex:'1 1 0',minHeight:0,display:'flex',flexDirection:'column',height:'400px',padding:'1rem'}}>
              <InternalChat
                brandId={openChatBrand.id}
                key={openChatBrand.id}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Modal for brand metadata management */}
      <BrandMetadataModal
        show={showMetadataModal}
        onHide={() => {
          setShowMetadataModal(false);
          setSelectedBrandId(null);
        }}
        brandId={selectedBrandId}
        onUpdateSuccess={handleMetadataUpdateSuccess}
      />

      {/* Modal for payment link management */}
      <PaymentLinkModal
        brand={selectedBrandForPayment}
        isOpen={showPaymentLinkModal}
        onClose={() => {
          setShowPaymentLinkModal(false);
          setSelectedBrandForPayment(null);
        }}
      />
    </div>
  );
}
